/**
 * i18n public API (I18N-T1).
 *
 * Importing this module initializes i18next (side-effect of ./config). Exposes the
 * language switch + the role-based leak guard that keeps non-student portals English.
 *
 * @module i18n
 */
import i18n, { STORAGE_KEY, type SupportedLang } from './config';
import api from '../services/api';

export { STORAGE_KEY };
export type { SupportedLang };
export { default as i18n } from './config';

/** The ONLY languages offered by the switcher — the two we actually translate. */
export const SUPPORTED_LANGS: ReadonlyArray<{ code: SupportedLang; label: string; native: string }> = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिंदी' },
];

export function isSupportedLang(code: unknown): code is SupportedLang {
  return code === 'en' || code === 'hi';
}

/**
 * The device's last language choice (localStorage). Used pre-login (the user isn't known yet) and
 * as the source a STUDENT's FIRST authenticated login adopts + stamps onto the account (LANG-2).
 */
export function getDeviceLanguage(): SupportedLang {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'hi' ? 'hi' : 'en';
  } catch {
    return 'en';
  }
}

/**
 * Switch the UI language now (user-initiated, from the switcher). Persists to
 * localStorage for instant/offline boot, and — when authenticated — syncs the choice
 * to the account via PATCH /language/preference so it follows the user to any device.
 */
export async function changeAppLanguage(code: string, opts: { persistServer?: boolean } = {}): Promise<void> {
  const lang: SupportedLang = isSupportedLang(code) ? code : 'en';
  try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* storage unavailable — non-fatal */ }
  await i18n.changeLanguage(lang);
  if (opts.persistServer) {
    try { await api.patch('/language/preference', { language: lang }); } catch { /* best-effort cross-device sync */ }
  }
}

/**
 * LEAK GUARD — apply the correct language for the authenticated role, called on login
 * and on session-restore.
 *
 * Only the STUDENT portal (and the logged-out auth surfaces) is translated, so:
 *  - STUDENT  → if the account has an EXPLICIT choice (chosenAt set), it wins on any device;
 *               otherwise fall back to THIS device's choice (so a pre-login Hindi pick is NOT
 *               clobbered by the account's unset 'en' default — the LANG-2 bug fix). The first-login
 *               adopt+stamp that persists the device choice to the account lives in AuthContext.
 *  - any other role (ADMIN/SUPER_ADMIN/EMPLOYEE/APP_OWNER/SUPPORT_STAFF) → force English,
 *    WITHOUT clobbering localStorage (so a student sharing the device keeps their choice).
 *
 * i18next's active language is global to the app instance; this is what prevents a shared
 * component (e.g. SessionProgressCard) from leaking Hindi into a staff portal.
 */
export function applyLanguageForRole(role: string | undefined | null, preferred?: string | null, chosenAt?: string | null): void {
  if (role === 'STUDENT') {
    const target: SupportedLang = chosenAt && isSupportedLang(preferred) ? preferred : getDeviceLanguage();
    try { localStorage.setItem(STORAGE_KEY, target); } catch { /* non-fatal */ }
    void i18n.changeLanguage(target);
  } else {
    void i18n.changeLanguage('en'); // never touch localStorage here (staff portals are EN-only)
  }
}
